import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Inject,
  Injector,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map } from 'rxjs/operators';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AbstractMethodComponent } from '../abstract-method.component';
import {
  CustomFormData,
  InterfacePaymentProviderStipe,
} from '@orderform/widgets';

interface StripeElement {
  addEventListener: (
    type: string,
    listener: (event: {
      complete?: boolean;
      error?: { message: string };
    }) => void
  ) => void;

  mount(selector: string): void;
}

interface StripeElementsCreator {
  create(type: string, options: any): StripeElement;
}

interface StripeInstance {
  elements(): StripeElementsCreator;

  confirmCardSetup(
    secret: string,
    data: any,
    config: any
  ): Promise<{
    error: { message: string };
    setupIntent: { id: { card: any } };
  }>;
}

interface StripeStyles {
  textColor: string;
  inputFieldColor: string;
  placeholderColor: string;
  fontSize: string;
}

interface StripeAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
}

const DEFAULT_STYLES: StripeStyles = {
  textColor: 'rgba(4, 37, 62, 0.8)',
  inputFieldColor: 'rgba(255, 255, 255, 1)',
  placeholderColor: 'rgba(4, 37, 62, 0.3)',
  fontSize: '1',
};

@Component({
  selector: 'orderform-payment-method-stripe',
  templateUrl: './stripe.component.html',
  styleUrls: ['./stripe.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StripeComponent),
      multi: true,
    },
  ],
})
export class StripeComponent
  extends AbstractMethodComponent
  implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit
{
  @Input() config: InterfacePaymentProviderStipe;

  @Input() styles?: StripeStyles = DEFAULT_STYLES;

  public stripeError$ = {
    number: new BehaviorSubject<string>(''),
    expire: new BehaviorSubject<string>(''),
    cvc: new BehaviorSubject<string>(''),
  };
  private _stripeComplete = {
    number: false,
    expire: false,
    cvc: false,
  };
  private _subscriptionBeforeSubmit =
    this._orderformFascade.beforeSubmit.subscribe(() => this._onBeforeSubmit());
  private _stripeInstance: StripeInstance;
  private _stripeCardNumber: StripeElement;
  private _stripeCardExpire: StripeElement;
  private _stripeCardCvc: StripeElement;
  private _stripeToken: { card: any };
  private _waitStripeInitialized: Promise<boolean>;

  constructor(
    private _orderformFascade: OrderformFascade,
    @Inject(LOCALE_ID) private _locale: string,
    injector: Injector
  ) {
    super(injector);
  }

  getStripe(): (
    publicKey: string,
    config: { locale: string }
  ) => StripeInstance {
    return (<any>window).Stripe;
  }

  ngOnInit(): void {
    this._waitStripeInitialized = (async () => {
      if (!this.getStripe()) {
        await this._loadExternalResources([this.config.apiUrl], [], true);
      }

      /**
       * get Stripe Function from asset load (_loadExternalResources)
       */
      const stripeFunction = this.getStripe();

      /**
       * Check stripe has loaded right
       */
      if (!stripeFunction) {
        console.log('Stripe not exists');
        return false;
      }

      /**
       * create instance
       */
      this._stripeInstance = stripeFunction(this.config.publicKey, {
        locale: this._locale.replace(/-[a-zA-Z0-9]{2,3}$/, ''),
      });

      const elements = this._stripeInstance.elements();
      const style = {
        base: {
          iconColor: this.styles.textColor,
          color: this.styles.textColor,
          fontSize: parseFloat(this.styles.fontSize) * 16 + 'px', // send as px; 1rem = 16px
          ':-webkit-autofill': {
            color: this.styles.placeholderColor,
          },
          '::placeholder': {
            color: this.styles.placeholderColor,
          },
        },
        invalid: {
          iconColor: this.styles.textColor,
          color: this.styles.textColor,
        },
      };

      const classes = {
        complete: 'ng-valid',
        invalid: 'ng-invalid',
      };

      this._stripeCardNumber = elements.create('cardNumber', {
        placeholder: '4444 3333 2222 1111',
        showIcon: true,
        style,
        classes,
      });

      this._stripeCardExpire = elements.create('cardExpiry', {
        placeholder: '03 / ' + ('' + (new Date().getFullYear() + 2)).slice(-2),
        style,
        classes,
      });

      this._stripeCardCvc = elements.create('cardCvc', {
        placeholder: '●●●',
        style: {
          ...style,
          base: {
            ...style.base,
            ':-webkit-autofill': {
              color: '#E6E9EC',
            },
            '::placeholder': {
              color: '#E6E9EC',
            },
          },
        },
        classes,
      });

      const fields = {
        number: this._stripeCardNumber,
        expire: this._stripeCardExpire,
        cvc: this._stripeCardCvc,
      };
      Object.keys(fields).forEach((field) => {
        fields[field].addEventListener('change', (event) => {
          this._stripeComplete[field] = event.complete;
          this._paymentDataChanged.next({ valid: this._isValid() });
          if (event.error) {
            this.stripeError$[field].next(event.error.message);
          } else {
            this.stripeError$[field].next('');
          }
        });
      });

      return true;
    })();
  }

  public async ngAfterViewInit() {
    if ((await this._waitStripeInitialized) === false) {
      return;
    }
    this._stripeCardNumber.mount(
      '#stripe-card-number-' + this.uniqueInstanceId
    );
    this._stripeCardExpire.mount(
      '#stripe-card-expire-' + this.uniqueInstanceId
    );
    this._stripeCardCvc.mount('#stripe-card-cvc-' + this.uniqueInstanceId);
  }

  private _onBeforeSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }
    if (this._stripeToken) {
      this._paymentDataChanged.next({
        valid: true,
        stripe_token: this._stripeToken,
      });
      return Promise.resolve({
        valid: true,
      });
    } else {
      this._paymentDataChanged.next({
        valid: false,
      });
    }

    const notCompletedFields = Object.keys(this._stripeComplete).filter(
      (field) => !this._stripeComplete[field]
    );
    if (!this._isValid()) {
      // show generic error
      notCompletedFields.forEach((field) => {
        if (this.stripeError$[field].getValue() === '') {
          this.stripeError$[field].next(
            $localize`:@@payment_method.label.stripe_incomplete_generic:Die Eingabe wird benötigt.`
          );
        }
      });
      return Promise.resolve({ valid: false });
    }
    // Get Stripe Token
    return firstValueFrom(
      this._orderformFascade.order$.pipe(map((order) => order.order))
    ).then((order) => {
      const payment_method: { card: any; billing_details?: any } = {
        card: this._stripeCardNumber,
      };

      payment_method.billing_details = {
        address: this._mapToStripeAddress(
          order.has_billing_address === 0
            ? order.shipping_address
            : order.billing_address
        ),
      };

      return this._stripeInstance
        .confirmCardSetup(
          this.config.intentClientSecret,
          { payment_method },
          {
            handleActions: false,
          }
        )
        .then((result) => {
          if (result.error) {
            console.log('Stripe Error:', result.error);
            this._toaster.error(
              $localize`:@@payment_method.error.generic:Die Zahlung konnte jetzt nicht verarbeitet werden. Bitte probieren es mit einer anderen Bezahlweise nochmal.`
            );
            return { valid: false };
          } else {
            this._stripeToken = result.setupIntent.id;
            this._paymentDataChanged.next({
              valid: true,
              stripe_token: result.setupIntent.id,
            });
            return { valid: true };
          }
        });
    });
  }

  private _mapToStripeAddress(addressInput: CustomFormData): StripeAddress {
    const stripeAddress: StripeAddress = {};
    if (!addressInput) {
      return stripeAddress;
    }

    const StripeAddressMapping: Partial<{
      [P in keyof StripeAddress]: string[];
    }> = {
      line1: ['buyer_address_street', 'buyer_address_street_billing'],
      line2: ['buyer_address_street2', 'buyer_address_street2_billing'],
      city: ['buyer_address_city', 'buyer_address_city_billing'],
      state: ['buyer_address_state', 'buyer_address_state_billing'],
      country: [
        'buyer_address_private_country',
        'buyer_address_private_country_billing',
        'country',
        'country_billing',
        'address_country',
        'address_country_billing',
        'buyer_address_country',
        'buyer_address_country_billing',
      ],
      postal_code: [
        'buyer_address_private_zipcode',
        'buyer_address_private_zipcode_billing',
        'buyer_address_zipcode',
        'buyer_address_zipcode_billing',
      ],
    };

    Object.keys(StripeAddressMapping).forEach((stripeKey) => {
      const addressInputKeys = StripeAddressMapping[stripeKey];
      const foundAddressInputKey = addressInputKeys.find((addressInputKey) =>
        addressInput.hasOwnProperty(addressInputKey)
      );
      if (foundAddressInputKey && addressInput[foundAddressInputKey]) {
        stripeAddress[stripeKey] = addressInput[foundAddressInputKey];
      }
    });

    return stripeAddress;
  }

  private _isValid() {
    const notCompletedFields = Object.keys(this._stripeComplete).filter(
      (field) => !this._stripeComplete[field]
    );
    const notCompleted = notCompletedFields.length > 0;
    return !notCompleted;
  }

  ngOnDestroy() {
    this._subscriptionBeforeSubmit.unsubscribe();
  }
}
